// © 2025 NM Software Development UG (haftungsbeschränkt)
// All rights reserved.

import {ChangeDetectionStrategy, Component, input, model} from '@angular/core';

/** One rendered segment: its value, the words on it, and whether it may be chosen at all. */
export interface SegmentedChoiceOption<TValue> {
  readonly value: TValue;
  readonly label: string;
  /** `true` renders the segment greyed and unpickable. Prefer it to removing the segment — see the class doc. */
  readonly disabled?: boolean;
}

/**
 * A tab-picker for a form's own state: two or three peer choices in one strip, one of them on (#664).
 *
 * ### Radios, not tabs, and not buttons
 *
 * It *looks* like the app's tab strip (`app-tab-selection`) and is deliberately NOT one. A tablist is for peer
 * **views of a page** — it puts the choice in the URL and each segment owns a `tabpanel`. This asks which
 * **intention the form is being filled in for**: the choice belongs to the form's state, it changes which
 * fields the form shows, and it is submitted with it. Wiring that into a tablist means either a URL that
 * changes when nothing was navigated, or a `role="tab"` with no panel behind it.
 *
 * It is also not a row of `<button aria-pressed>`. Real `<input type="radio">`s bring the grouped announcement
 * („Art des Events, Kassen-Event, ausgewählt, 1 von 2"), the roving tab stop and arrow-key movement **for
 * free**, natively and correctly, on every platform — and `aria-checked` is implicit in `checked`, so the two
 * can never disagree. The style guide has said this since #581 ("Segmented choice inside a form"); this
 * component is that pattern extracted out of `invite-delivery-choice`, which is the same thing specialised for
 * one screen.
 *
 * ### The skin: a field that happens to hold two segments
 *
 * A **white** track with a grey edge (`bg-white border-[1px] border-solid border-gray-300 rounded-md
 * shadow-sm`) and the chosen segment as a filled `bg-theme-primary-700` pill inset by the track's own 2 px
 * padding, so the track shows around it like a frame. The fill is the same "selected" grammar the weekday
 * buttons in the events form and the submit button already use, so a form does not carry two different ideas
 * of what "on" looks like.
 *
 * **White, not grey — and that is a bug fix, not a preference.** The first cut used a `bg-gray-100` track.
 * This control's home is a form panel that is *itself* `bg-gray-100`, so the track vanished into it: the
 * selected pill floated on the panel with nothing around it and the unselected half read as a caption rather
 * than as something you could press. A white surface with a grey edge is also exactly what the text inputs
 * beside it are, which is the whole "matching the style" claim.
 *
 * **`h-9` (36 px), matched to those inputs.** The form's text fields are `p-1.5` + `sm:text-sm sm:leading-6`
 * = 12 + 24 px, and a control that stood 8 px taller than the field next to it is what made the first cut
 * look bolted on. The segments take `h-full`, so the pill's height is the track's minus its border and
 * padding — no second number to keep in step. `flex-1` splits the row evenly, so two long German labels share
 * it instead of one of them setting the width.
 *
 * **On the 44 px tap target.** 36 px clears the WCAG 2.2 AA minimum (24 × 24 px) comfortably. The 44 px
 * figure is platform *guidance*, and it is met the way WCAG actually allows — by spacing: the control sits in
 * a grid row with `gap-y-4` above and below and has no adjacent target within 16 px, so a fingertip that
 * misses low or high hits nothing else. Matching the neighbouring field's height is worth more here than
 * inflating one control past every other input on the form.
 *
 * | | Text | Surface | Ratio |
 * | --- | --- | --- | --- |
 * | selected | `text-white` | `bg-theme-primary-700` `#be185d` | **6.04:1** |
 * | unselected | `text-gray-700` `#374151` | `bg-white` | **10.31:1** |
 * | unselected, hovered | `text-gray-700` `#374151` | `bg-gray-100` `#f3f4f6` | **9.37:1** |
 *
 * All clear AA at `text-sm`. Note the app's older toolbar segment (`text-theme-primary-600` on
 * `bg-theme-primary-50`) is only **4.21:1** and does NOT — that is why this control does not reuse it.
 *
 * ### The focus outline is not optional, and it is an outline for a reason
 *
 * The real input is `sr-only`, so the only visible focus a keyboard user can get is the one the wrapping
 * `<label>` draws. It is an `outline`, not a `ring`: the track's gutter is 2 px, so an offset ring would be
 * clipped by the border — and an *inset* ring on the selected segment is a theme-coloured line drawn on a
 * theme-coloured pill, which is to say invisible. An outline is painted outside the pill, over the gutter,
 * and reads against both the white track and the filled pill.
 *
 * ### Two gotchas that bite silently
 *
 * * **`name` is required and must be unique in the document.** Two radio groups sharing a name are ONE group:
 *   a page rendering two of these would silently deselect the other's choice.
 * * **Enter is swallowed.** These live inside `<form>`s, where Enter submits. A picker where choosing the
 *   segment you are standing on submits the form is a trap, so Enter selects and stops there. Space already
 *   selects natively; arrow keys already move *and* select (automatic activation), which is the correct
 *   radio-group behaviour and needs no code at all.
 *
 * ```html
 * <cqme-segmented-choice legend="Art des Events" name="event-kind" [options]="kindOptions"
 *                        [value]="kind" (valueChange)="onKindChanged($event)"/>
 * ```
 */
@Component({
  selector: 'cqme-segmented-choice',
  standalone: true,
  templateUrl: './segmented-choice.component.html',
  // `block`, because a custom element is `display: inline` by default and would not fill the grid cell a form
  // places it in — the strip would shrink-wrap its labels and sit left of the field above it.
  host: {class: 'block'},
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SegmentedChoiceComponent<TValue> {
  /**
   * The chosen value. A `model`, so a caller may two-way bind it or — where the value is *derived* from
   * something else, as the events form's kind is derived from its draft event — bind it one-way and answer
   * the output. Both are supported on purpose; the one-way form is what keeps a single source of truth.
   */
  readonly value = model.required<TValue>();

  /** The segments, in display order. Two or three; more belongs in a `cqme-select`, not on one row. */
  readonly options = input.required<readonly SegmentedChoiceOption<TValue>[]>();

  /**
   * The visible group heading, rendered as the fieldset's `<legend>`.
   *
   * A `<legend>`, never a `<label for>`: `for` must point at a labelable element, and a radio *group* is not
   * one. The legend names the group natively, which is what makes the screen-reader announcement read
   * „Art des Events, Kassen-Event, ausgewählt" instead of just „Kassen-Event".
   */
  readonly legend = input.required<string>();

  /**
   * The radio group's `name`, and the stem of each segment's `id` (`event-kind-0`, `-1`, …).
   *
   * Required and per-screen — see the class doc's first gotcha. It is not derived from a counter because a
   * generated name is invisible in the DOM you are debugging and in the spec you are writing.
   */
  readonly name = input.required<string>();

  protected isSelected(option: SegmentedChoiceOption<TValue>): boolean {
    return this.value() === option.value;
  }

  /** The segment's `id`, by position: a value is not guaranteed to be a usable id fragment (it may be an enum). */
  protected segmentId(index: number): string {
    return `${this.name()}-${index}`;
  }

  protected choose(option: SegmentedChoiceOption<TValue>): void {
    if (option.disabled) return;
    this.value.set(option.value);
  }

  /**
   * Enter selects — and, above all, does not reach the surrounding `<form>`.
   *
   * Arrow keys and Space are deliberately absent from this handler: the browser already implements them for a
   * radio group, correctly and per platform. Re-implementing them here would add a second, slightly different
   * answer to the same key.
   */
  protected onEnter(event: Event, option: SegmentedChoiceOption<TValue>): void {
    event.preventDefault();
    this.choose(option);
  }
}
